import {
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import SupplierProfileHeader from "./ModalContent/SupplierProfileHeader";
import SupplierInfo from "./ModalContent/SupplierInfo";
import ModalActionButtons from "./ModalContent/ModalActionButtons";
import {
  dialogStyle,
  dialogContentStyle,
  supplierFooterStyle,
  dialogBox,
  rightSectionStyle,
  actionButtonStyle,
  declineActionBtn,
  detailBackdropStyle,
} from "./styles/styles";
import PricelistsInfo from "./ModalContent/PricelistsInfo";
import ProductsInfo from "./ModalContent/ProductsInfo";
import ProductDetailPanel from "./ModalContent/ProductDetailPanel";
import SupplierAcceptedModal from "./SupplierAcceptedModal";
import { categoryNamespaces } from "../../Helpers/CategoryNamespaces";
import RemoveSupplierModal from "./RemoveSupplierModal";
import DiscardChangesModal from "./DiscardChangesModal";
import CustomTabs from "../../Components/CustomTabs";
import { useSnackbar } from "../../Components/SnackBar";
import { useTranslation } from "react-i18next";
import {
  inviteSupplier,
  acceptOffer,
  updateSupplierContract,
  removeSupplier,
  removeSupplierOffer,
  getSupplierProducts,
  acceptWholeOffer,
  getSupplierOpenOrders,
  pauseSupplier,
} from "./Helpers";
import { isRenderablePricelist } from "./Helpers/pricelistContent";
import InviteModal from "../../Components/Modals/InviteModal";
import MaximumCapacityReachedModal from "../../Components/Modals/MaximumCapacityReachedModal";
import { Button } from "@mui/material";
import CustomTypography from "../../Components/Typography";

const SupplierProfileModal = ({
  viewType = "invite",
  // "active" / "pending" when the supplier can't be invited from here — they
  // already work with (or are joining) this community for another catalog.
  inviteBlockedReason = null,
  openSupplierProfileModal,
  tableConfig,
  supplierProfileData,
  handleCloseSupplierProfileModal,
  setSupplierStatusChanged,
  setRefreshSuppliersData,
  handleConfirmModal,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Matches ModalActionButtons: below 450px the footer buttons go full-width.
  const isCustomScreen = useMediaQuery(theme.breakpoints.down("450"));
  const showSnackbar = useSnackbar();
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openSupplierAccept, setOpenSupplierAccept] = useState(false);
  // Name of the supplier whose approval was refused at its members cap; the
  // capacity popup is open while it is set.
  const [capacityBlockedSupplier, setCapacityBlockedSupplier] = useState(null);
  // What the CM just approved, localized, for the confirmation modal. Each entry
  // carries its vertical so the blurb can name what was approved ("services and
  // products") rather than calling everything a service.
  const [acceptedItems, setAcceptedItems] = useState([]);
  const [openRemoveSupplier, setOpenRemoveSupplier] = useState(false);
  // Orders still running for this supplier in this community. Removing a
  // supplier cancels their pricelists and discounts, so a supplier with open
  // orders can only be paused until those orders close — the remove modal turns
  // into a pause-instead prompt while this is non-zero.
  const [openOrdersCount, setOpenOrdersCount] = useState(0);
  const [pausingSupplier, setPausingSupplier] = useState(false);
  const [openDiscardChangesModal, setOpenDiscardChangesModal] = useState(false);
  const [selectedPricelists, setSelectedPricelists] = useState(() =>
    supplierProfileData.pricelists
      .filter((p) =>
        viewType === "view" ? p.is_offered && p.is_accepted : p.is_offered
      )
      .map((p) => p.subcategory_id)
  );

  const { t } = useTranslation([
    "supplier-profile-modal",
    "invite-modal",
    "supplier-products-info",
    "modal-action-buttons",
  ]);

  const [expandedAccordion, setExpandedAccordion] = useState(null);

  // Reviewing an offer (a fresh one, or an active supplier's contract) is the
  // only flow with catalog steps — and the only one where "included in the
  // offer" is a meaningful distinction.
  const isOfferReview = viewType === "offer" || viewType === "view";

  const [productsData, setProductsData] = useState(null);
  // Products load asynchronously; track it so we can show a spinner instead of
  // briefly flashing the services step while we wait (see desktop body below).
  const [productsLoading, setProductsLoading] = useState(isOfferReview);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  // Restaurants mirror products — same payload shape, different vertical.
  const [restaurantsData, setRestaurantsData] = useState(null);
  const [selectedRestaurantCategoryIds, setSelectedRestaurantCategoryIds] =
    useState([]);
  const [detailProduct, setDetailProduct] = useState(null);
  // Active tab index on mobile (CustomTabs); drives which footer to show there.
  const [mobileTabIndex, setMobileTabIndex] = useState(0);

  const { supplier, pricelists, is_paused, paused_by, supplier_state } =
    supplierProfileData;

  // Read the tally up front so the remove modal can explain the block instead of
  // failing on submit. A failed read leaves the count at 0 and the CM sees the
  // normal remove flow — the API rejects it with 409 anyway (handled below).
  useEffect(() => {
    if (viewType !== "view" || !openSupplierProfileModal || !supplier?.id) return;
    let cancelled = false;
    getSupplierOpenOrders(supplier.id)
      .then((response) => {
        if (!cancelled) setOpenOrdersCount(response?.data?.total ?? 0);
      })
      .catch((error) => {
        console.error("Open orders fetch error:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [viewType, openSupplierProfileModal, supplier?.id]);

  const productCategories = productsData?.product_categories || [];
  const restaurantCategories = restaurantsData?.product_categories || [];
  // Counts for the step footers/header reflect the *offer* (selectable categories),
  // so non-offered "not included" categories don't inflate the total.
  const offeredProductCount = productCategories.filter(
    (c) => c.is_offered
  ).length;
  const offeredRestaurantCount = restaurantCategories.filter(
    (c) => c.is_offered
  ).length;

  /**
   * A step only exists when the supplier has at least one item of that kind
   * *included in the offer* (COMY1-333). Owning products the offer leaves out
   * used to be enough to get a Products step, so the CM was walked through a
   * step whose entire content sat under "not included in the offer".
   *
   * Catalog rows load asynchronously, so the offered tally comes from the
   * profile fetch: the stepper (header, tabs, footer navigation) then renders
   * its full set on open instead of reflowing a beat later. The async rows are
   * the fallback for a backend that hasn't shipped the counts yet.
   */
  const offeredCategoryCount = (serverCounts, categories) =>
    serverCounts
      ? serverCounts.offered
      : categories.filter((c) => c.is_offered).length;

  const productsStepExists =
    isOfferReview &&
    offeredCategoryCount(
      supplierProfileData?.product_category_counts,
      productCategories
    ) > 0;
  const restaurantsStepExists =
    isOfferReview &&
    offeredCategoryCount(
      supplierProfileData?.restaurant_category_counts,
      restaurantCategories
    ) > 0;
  // Same rule for services, using the pricelists the fetch already carries. A
  // pricelist has to be renderable too — an offered but still-unpriced one shows
  // nothing, so it can't be what keeps the step alive. Outside the offer review
  // (invite / cancel / view-as-CM) there is no offer to filter by, so the step
  // shows whenever the supplier has pricelists at all.
  const servicesStepExists = isOfferReview
    ? pricelists.some((p) => p.is_offered && isRenderablePricelist(p))
    : pricelists.length > 0;

  // Ordered list of the steps this supplier actually has. The body, footer and
  // header all derive from it, so a third step can't fall out of sync with the
  // navigation the way a hardcoded services→products pair would.
  const stepOrder = [
    servicesStepExists && "services",
    productsStepExists && "products",
    restaurantsStepExists && "restaurants",
  ].filter(Boolean);

  const [activeStep, setActiveStep] = useState(stepOrder[0] || "services");

  // A step can still appear a beat late when the backend hasn't shipped the
  // offered-category tallies and the async rows are what reveal it; keep the
  // active step inside the set that actually exists.
  const stepOrderKey = stepOrder.join("|");
  useEffect(() => {
    if (stepOrder.length > 0 && !stepOrder.includes(activeStep)) {
      setActiveStep(stepOrder[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepOrderKey, activeStep]);

  /**
   * Stepper-chip counts. Until the details rows land, `productCategories` /
   * `restaurantCategories` are empty and every chip read "(0/0)" before jumping
   * to the real numbers — so fall back to the tallies the profile fetch already
   * carries, which count the same set. Once the rows are in, the live selection
   * wins so the chip tracks the CM's clicks.
   *  - selected: seeded the same way `preselect` seeds it (accepted in "view"
   *    mode, offered while reviewing an offer).
   *  - total: the offered categories.
   */
  const stepCounts = (categories, selectedIds, serverCounts) => {
    if (categories.length > 0 || !serverCounts) {
      return {
        selected: selectedIds.length,
        total: categories.filter((c) => c.is_offered).length,
      };
    }
    return {
      selected:
        viewType === "view" ? serverCounts.accepted : serverCounts.offered,
      total: serverCounts.offered,
    };
  };
  const productStepCounts = stepCounts(
    productCategories,
    selectedCategoryIds,
    supplierProfileData?.product_category_counts
  );
  const restaurantStepCounts = stepCounts(
    restaurantCategories,
    selectedRestaurantCategoryIds,
    supplierProfileData?.restaurant_category_counts
  );

  // "Average Discount For Your Community" badge = mean of every offered discount
  // across services, store and menu. The profile fetch carries it, so the badge
  // is right on first paint — computing it here from the async rows meant it
  // showed a services-only figure and then jumped once the catalog arrived (and
  // silently left the menu out of the mean entirely). The local computation
  // stays as a fallback for a backend that hasn't shipped the field yet, and now
  // counts restaurants too.
  const fallbackOfferedDiscounts = [
    ...pricelists
      .filter((p) => p.is_offered && p.discount_rate != null)
      .map((p) => Number(p.discount_rate)),
    ...productCategories
      .filter((c) => c.is_offered && c.discount_rate != null)
      .map((c) => Number(c.discount_rate)),
    ...restaurantCategories
      .filter((c) => c.is_offered && c.discount_rate != null)
      .map((c) => Number(c.discount_rate)),
  ];
  const fallbackAverageDiscount = fallbackOfferedDiscounts.length
    ? Math.round(
        (fallbackOfferedDiscounts.reduce((sum, d) => sum + d, 0) /
          fallbackOfferedDiscounts.length) *
          100
      ) / 100
    : null;
  const averageDiscount =
    supplierProfileData?.average_discount !== undefined
      ? supplierProfileData.average_discount
      : fallbackAverageDiscount;

  useEffect(() => {
    let active = true;
    if (!isOfferReview || !supplier?.id) {
      setProductsLoading(false);
      return;
    }
    setProductsLoading(true);
    (async () => {
      try {
        const [response, restaurantResponse] = await Promise.all([
          getSupplierProducts(supplier.id),
          getSupplierProducts(supplier.id, "restaurants"),
        ]);
        if (!active) return;
        // Mirror the services step: for an active supplier (view) pre-select only the
        // already-accepted categories; for a fresh offer review, all offered ones.
        const preselect = (cats) =>
          (cats || [])
            .filter((c) => (viewType === "view" ? c.is_accepted : c.is_offered))
            .map((c) => c.category_id);

        const data = response?.data || {};
        setProductsData(data);
        setSelectedCategoryIds(preselect(data.product_categories));

        const restaurantData = restaurantResponse?.data || {};
        setRestaurantsData(restaurantData);
        setSelectedRestaurantCategoryIds(
          preselect(restaurantData.product_categories)
        );
      } catch (error) {
        console.error("Failed to load supplier products:", error);
      } finally {
        if (active) setProductsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [supplier?.id, isOfferReview, viewType]);

  // Re-clicking the open product closes the detail panel (the desktop close path,
  // since the panel's own close button is mobile-only).
  const handleToggleDetail = (product) =>
    setDetailProduct((prev) => (prev?.id === product.id ? null : product));

  const handleToggleCategory = (category) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(category.category_id)
        ? prev.filter((id) => id !== category.category_id)
        : [...prev, category.category_id]
    );
  };

  const activeStepIndex = stepOrder.indexOf(activeStep);
  // Footer counters follow the step being reviewed, so the restaurants step
  // never shows product numbers.
  const stepSelectedCount =
    activeStep === "restaurants"
      ? selectedRestaurantCategoryIds.length
      : selectedCategoryIds.length;
  const stepTotalCount =
    activeStep === "restaurants" ? offeredRestaurantCount : offeredProductCount;
  const isLastStep = activeStepIndex === stepOrder.length - 1;
  const nextStep = stepOrder[activeStepIndex + 1];
  // Back walks the same list. It used to be hardcoded to "services", which was
  // fine while Products was always the last step — with Restaurants added,
  // Products became a middle step and lost its Back button entirely, and the
  // Restaurants step skipped straight past Products.
  const prevStep = activeStepIndex > 0 ? stepOrder[activeStepIndex - 1] : null;

  /**
   * The checkboxes are on category GROUPS (the parent category), whose
   * `category_id` is the parent id — but an offer row is keyed by the LEAF
   * category the item actually sits in. Approving by the group id therefore
   * matches no rows, so expand each selected group to its items' leaf ids.
   */
  const leafCategoryIdsOf = (categories, selectedGroupIds) => [
    ...new Set(
      (categories || [])
        .filter((c) => selectedGroupIds.includes(c.category_id))
        .flatMap((c) => (c.products || []).map((p) => p.category_id))
        .filter((id) => id != null)
    ),
  ];

  const handleToggleRestaurantCategory = (category) => {
    setSelectedRestaurantCategoryIds((prev) =>
      prev.includes(category.category_id)
        ? prev.filter((id) => id !== category.category_id)
        : [...prev, category.category_id]
    );
  };

  // The accept unit is the product's own category_id (the offer key in
  // products_discounts_communities); map the selected display categories to the
  // distinct leaf category ids of their products.
  const getSelectedProductCategoryIds = () => {
    const selectedSet = new Set(selectedCategoryIds);
    const leafIds = productCategories
      .filter((c) => selectedSet.has(c.category_id))
      .flatMap((c) => c.products.map((p) => p.category_id));
    return [...new Set(leafIds)];
  };

  // Final step of the offer review: persist the services selection from step 1
  // (per view type) and the selected product categories from step 2.
  const handleAcceptAll = async () => {
    const categoryIds = getSelectedProductCategoryIds();
    if (
      !selectedPricelists.length &&
      !categoryIds.length &&
      !selectedRestaurantCategoryIds.length
    ) {
      showSnackbar(t("no_products_selected", { ns: "supplier-products-info" }), "error");
      return;
    }
    try {
      const productLeafIds = leafCategoryIdsOf(productCategories, categoryIds);
      // Guarded on the step existing: never approve restaurant categories the
      // CM was never shown.
      const restaurantLeafIds = leafCategoryIdsOf(
        restaurantCategories,
        selectedRestaurantCategoryIds
      );

      // Editing an existing contract is a different operation from approving an
      // offer, so it keeps its own call.
      const isContractEdit = viewType === "view" && selectedPricelists.length > 0;
      if (isContractEdit) {
        await updateSupplierContract(supplier.id, selectedPricelists);
      }

      /**
       * Approving the offer is ONE action, so it is one request: the backend
       * applies services + store + menu in a single transaction and notifies the
       * supplier once. This used to be up to three sequential POSTs, which could
       * half-succeed (nothing spanned them) and sent one "offer approved"
       * notification per call.
       */
      await acceptWholeOffer(supplier.id, {
        services: isContractEdit ? [] : selectedPricelists,
        products: productLeafIds,
        restaurants: restaurantsStepExists ? restaurantLeafIds : [],
      });
      // Show the shareable confirmation rather than closing on a snackbar. The
      // modal's own close handler closes the profile behind it. This was lost
      // when approving became one call for all three verticals — the old
      // single-vertical path opened it and the replacement never did.
      // Catalog entries key on category_id/category_name, and the names come
      // from the CM's DISPLAY selection — `categoryIds` above is already mapped
      // down to the leaf ids the API accepts, which don't appear in this list.
      const itemsOf = (list, selectedIds, vertical, kind) =>
        (list || [])
          .filter((c) => selectedIds.includes(c.category_id))
          .map((c) => ({
            kind,
            name: t(c.category_name, {
              ns: categoryNamespaces(vertical),
              defaultValue: c.category_name,
            }),
          }))
          .filter((i) => i.name);
      setAcceptedItems([
        ...(supplierProfileData?.pricelists || [])
          .filter((pl) => selectedPricelists.includes(pl.subcategory_id))
          .map((pl) => ({
            kind: "service",
            name: t(pl.subcategory_name, { ns: "categories" }),
          })),
        ...itemsOf(productCategories, selectedCategoryIds, "products", "product"),
        ...(restaurantsStepExists
          ? itemsOf(
              restaurantCategories,
              selectedRestaurantCategoryIds,
              "restaurants",
              "restaurant",
            )
          : []),
      ]);
      setRefreshSuppliersData && setRefreshSuppliersData(true);
      setOpenSupplierAccept(true);
    } catch (error) {
      // The supplier is already at its members cap: the server closed this
      // request (and its other open ones), so it leaves the pending list.
      if (error?.response?.data?.code === "SUPPLIER_MEMBERS_CAPACITY_REACHED") {
        setCapacityBlockedSupplier(supplier.name);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
        return;
      }
      showSnackbar(
        `${t("failed_to_accept", { ns: "supplier-products-info" })} ${supplier.name}`,
        "error"
      );
      console.error("Accept all error:", error);
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : null);
  };

  const handlePricelistToggle = (pricelistId) => () => {
    setSelectedPricelists((prev) =>
      prev.includes(pricelistId)
        ? prev.filter((id) => id !== pricelistId)
        : [...prev, pricelistId]
    );
  };

  const handleInviteClick = async () => {
    try {
      const response = await inviteSupplier(supplier.id);
      if (response.status === 200) {
        showSnackbar(`${supplier.name} ${t("has_been_invited")}`, "success");
        setOpenSuccess(true);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
      }
    } catch (error) {
      showSnackbar(`${t("failed_to_invite")} ${supplier.name}`, "error");
      console.error("Invite error:", error);
    }
  };

  const handleAcceptOffer = async () => {
    try {
      const response = await acceptOffer(supplier.id, selectedPricelists);
      if (response.status === 200) {
        setOpenSupplierAccept(true);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
      }
    } catch (error) {
      selectedPricelists.length
        ? showSnackbar(
            `${t("failed_to_accept_offer")} ${supplier.name}`,
            "error"
          )
        : showSnackbar(`No pricelists are selected`, "error");
      console.error("Accept offer error:", error);
    }
  };

  const handleUpdateSupplier = async () => {
    try {
      const response = await updateSupplierContract(
        supplier.id,
        selectedPricelists
      );
      if (response.status === 200) {
        showSnackbar(`${supplier.name} ${t("has_been_updated")}`, "success");
        setRefreshSuppliersData && setRefreshSuppliersData(true);
        handleCloseSupplierProfileModal();
      }
    } catch (error) {
      selectedPricelists.length
        ? showSnackbar(`${t("failed_to_update")} ${supplier.name}`, "error")
        : showSnackbar(`No pricelists are selected`, "error");
      console.error("Update supplier error:", error);
    }
  };

  const handleRemoveSupplierModal = () => {
    setOpenRemoveSupplier(true);
  };

  const handleSupplierRemoved = async (reason) => {
    try {
      const response = await removeSupplier(supplier.id, reason);
      if (response.status === 200) {
        showSnackbar(`${supplier.name} ${t("has_been_removed")}`, "success");
        setOpenRemoveSupplier(false);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
        handleCloseSupplierProfileModal();
      }
    } catch (error) {
      showSnackbar(`${t("failed_to_remove")} ${supplier.name}`, "error");
      console.error("Remove supplier error:", error);
    }
  };

  // Offered alongside removal while the supplier has open orders: pausing stops
  // new orders while keeping the supplier in the community.
  const handlePauseInstead = async () => {
    try {
      setPausingSupplier(true);
      const response = await pauseSupplier(supplier.id);
      if (response.status === 200) {
        showSnackbar(`${supplier.name} ${t("has_been_paused")}`, "success");
        setOpenRemoveSupplier(false);
        setSupplierStatusChanged?.((prev) => [
          ...prev.filter(
            (item) => item.supplierId !== Number(response.data.supplier_id)
          ),
          {
            supplierId: Number(response.data.supplier_id),
            status: "PAUSED_BY_CM",
          },
        ]);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
        handleCloseSupplierProfileModal();
      }
    } catch (error) {
      showSnackbar(`${t("failed_to_pause")} ${supplier.name}`, "error");
      console.error("Pause supplier error:", error);
    } finally {
      setPausingSupplier(false);
    }
  };

  const handleDeclineOfferModal = () => {
    setOpenRemoveSupplier(true);
  };

  const handleDeclineOffer = async (reason) => {
    try {
      const response = await removeSupplierOffer(supplier.id, reason);
      if (response.status === 200) {
        showSnackbar(`${supplier.name}${t("offer_declined")}`, "success");
        setOpenRemoveSupplier(false);
        setRefreshSuppliersData && setRefreshSuppliersData(true);
        handleCloseSupplierProfileModal();
      }
    } catch (error) {
      showSnackbar(`${t("failed_decline_offer")} ${supplier.name}`, "error");
      console.error("Decline offer error:", error);
    }
  };

  const handleDiscard = () => {
    setOpenDiscardChangesModal(false);
    handleCloseSupplierProfileModal();
  };

  const handleCloseSupplierAcceptModal = () => {
    setOpenSupplierAccept(false);
    handleCloseSupplierProfileModal();
  };

  const handleCloseInviteModal = () => {
    setOpenSuccess(false);
    handleCloseSupplierProfileModal();
  };

  const tabs = [
    {
      label: t("tabs.about"),
      content: (
        <SupplierInfo
          supplierInfoData={supplier}
          viewType={viewType}
          is_paused={is_paused}
          paused_by={paused_by}
          supplier_state={supplier_state}
          setSupplierStatusChanged={setSupplierStatusChanged}
        />
      ),
    },
    ...(servicesStepExists
      ? [
          {
            label: t("tabs.service_items"),
            content: (
              <PricelistsInfo
                {...{
                  pricelists,
                  viewType,
                  selectedPricelists,
                  handlePricelistToggle,
                  expandedAccordion,
                  handleAccordionChange,
                  tableConfig,
                }}
              />
            ),
          },
        ]
      : []),
    ...(restaurantsStepExists
      ? [
          {
            label: t("steps.restaurants", { ns: "supplier-products-info" }),
            content: (
              <Box sx={rightSectionStyle}>
                <ProductsInfo
                  vertical="restaurants"
                  {...{
                    productCategories: restaurantCategories,
                    selectedCategoryIds: selectedRestaurantCategoryIds,
                    onToggleCategory: handleToggleRestaurantCategory,
                    onOpenDetail: handleToggleDetail,
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
    ...(productsStepExists
      ? [
          {
            label: t("steps.products", { ns: "supplier-products-info" }),
            content: (
              <Box sx={rightSectionStyle}>
                <ProductsInfo
                  {...{
                    productCategories,
                    selectedCategoryIds,
                    onToggleCategory: handleToggleCategory,
                    onOpenDetail: handleToggleDetail,
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Dialog
        fullScreen={isSmallScreen}
        open={openSupplierProfileModal}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseSupplierProfileModal();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="1200px"
        sx={dialogStyle}
      >
        <Box sx={dialogBox}>
          <SupplierProfileHeader
            {...{
              viewType,
              supplierProfileData,
              selectedPricelists,
              onClose: handleCloseSupplierProfileModal,
              setOpenDiscardChangesModal,
              isSmallScreen,
              showServicesStep: servicesStepExists,
              showProductsStep: productsStepExists,
              showRestaurantsStep: restaurantsStepExists,
              activeStep,
              onStepChange: setActiveStep,
              averageDiscount: averageDiscount,
              productsSelectedCount: productStepCounts.selected,
              productsTotalCount: productStepCounts.total,
              restaurantsSelectedCount: restaurantStepCounts.selected,
              restaurantsTotalCount: restaurantStepCounts.total,
            }}
          />
          <DialogContent sx={{ ...dialogContentStyle, position: "relative" }}>
            {isMobile ? (
              <CustomTabs
                tabs={tabs}
                defaultTab={0}
                variant="fullWidth"
                onChange={(newValue) => setMobileTabIndex(newValue)}
              />
            ) : (
              <>
                <SupplierInfo
                  supplierInfoData={supplier}
                  viewType={viewType}
                  is_paused={is_paused}
                  paused_by={paused_by}
                  supplier_state={supplier_state}
                  setSupplierStatusChanged={setSupplierStatusChanged}
                />

                {activeStep !== "services" && productsLoading ? (
                  // Catalog rows load async; show a spinner rather than briefly
                  // flashing the services step before they arrive.
                  <Box
                    sx={{
                      ...rightSectionStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: "#257C6D" }} />
                  </Box>
                ) : activeStep === "restaurants" ? (
                  <Box sx={rightSectionStyle}>
                    <ProductsInfo
                      vertical="restaurants"
                      {...{
                        productCategories: restaurantCategories,
                        selectedCategoryIds: selectedRestaurantCategoryIds,
                        onToggleCategory: handleToggleRestaurantCategory,
                        onOpenDetail: handleToggleDetail,
                      }}
                    />
                  </Box>
                ) : activeStep === "products" ? (
                  <Box sx={rightSectionStyle}>
                    <ProductsInfo
                      {...{
                        productCategories,
                        selectedCategoryIds,
                        onToggleCategory: handleToggleCategory,
                        onOpenDetail: handleToggleDetail,
                      }}
                    />
                  </Box>
                ) : (
                  <PricelistsInfo
                    {...{
                      pricelists,
                      viewType,
                      selectedPricelists,
                      handlePricelistToggle,
                      expandedAccordion,
                      handleAccordionChange,
                      tableConfig,
                    }}
                  />
                )}
              </>
            )}

            {detailProduct && (
              <>
                <Box
                  sx={detailBackdropStyle}
                  onClick={() => setDetailProduct(null)}
                />
                <ProductDetailPanel
                  key={detailProduct.id}
                  product={detailProduct}
                  onClose={() => setDetailProduct(null)}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={supplierFooterStyle}>
            {!isMobile && !isLastStep && stepOrder.length > 1 ? (
              // Any step that still has one after it: continue to the next.
              <Box
                width="100%"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap="8px"
              >
                <Button
                  variant="contained"
                  sx={declineActionBtn}
                  onClick={
                    viewType === "view"
                      ? handleRemoveSupplierModal
                      : handleDeclineOfferModal
                  }
                >
                  {viewType === "view"
                    ? t("buttons.remove_supplier", { ns: "modal-action-buttons" })
                    : t("decline", { ns: "supplier-products-info" })}
                </Button>
                <Box display="flex" alignItems="center" gap="16px">
                  <CustomTypography
                    props={{
                      color: "#2F2B3DB2",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "28px",
                    }}
                  >
                    {/* The counter follows the step being reviewed: the
                        services step counts pricelists, a catalog step counts
                        categories. It used to always read "pricelists", which
                        was wrong once Products could sit in this branch. */}
                    {activeStep === "services" ? (
                      <>
                        {selectedPricelists.length}/
                        {pricelists.filter((p) => p.is_offered).length}{" "}
                        {t("buttons.pricelists_selected", {
                          ns: "modal-action-buttons",
                        })}
                      </>
                    ) : (
                      <>
                        {stepSelectedCount}/{stepTotalCount}{" "}
                        {t("categories_selected", {
                          ns: "supplier-products-info",
                        })}
                      </>
                    )}
                  </CustomTypography>
                  {prevStep && (
                    <Button
                      variant="outlined"
                      sx={{
                        ...actionButtonStyle,
                        backgroundColor: "transparent",
                        color: "rgba(62, 63, 76, 1)",
                        border: "1px solid rgba(62, 63, 76, 0.3)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                      }}
                      onClick={() => setActiveStep(prevStep)}
                    >
                      {t("button_back", { ns: "supplier-products-info" })}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    sx={actionButtonStyle}
                    onClick={() => nextStep && setActiveStep(nextStep)}
                  >
                    {t("continue", { ns: "supplier-products-info" })}
                  </Button>
                </Box>
              </Box>
            ) : (productsStepExists || restaurantsStepExists) &&
              (isMobile ? mobileTabIndex === tabs.length - 1 : isLastStep) ? (
              // Last catalog step: finalize the whole offer (services + catalog).
              // Checking only for the Products step here meant a supplier whose
              // offer is menu-only fell through to the services footer. Layout
              // mirrors ModalActionButtons so it matches the services footer (count on
              // top + full-width stacked buttons on mobile). On mobile the Products tab
              // is the last CustomTabs tab.
              <Box
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                {isSmallScreen && (
                  <CustomTypography
                    props={{
                      color: "#2F2B3DB2",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "28px",
                    }}
                  >
                    {stepSelectedCount}/{stepTotalCount}{" "}
                    {t("categories_selected", { ns: "supplier-products-info" })}
                  </CustomTypography>
                )}
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap="8px"
                  width="100%"
                >
                  <Button
                    variant="contained"
                    sx={{
                      ...declineActionBtn,
                      width: isCustomScreen ? "100%" : "auto",
                    }}
                    onClick={
                      viewType === "view"
                        ? handleRemoveSupplierModal
                        : handleDeclineOfferModal
                    }
                  >
                    {viewType === "view"
                      ? t("buttons.remove_supplier", {
                          ns: "modal-action-buttons",
                        })
                      : t("decline", { ns: "supplier-products-info" })}
                  </Button>
                  <Box
                    display="flex"
                    flexDirection={isCustomScreen ? "column" : "row"}
                    alignItems="center"
                    gap="16px"
                    width={isCustomScreen ? "100%" : "auto"}
                  >
                    {!isSmallScreen && (
                      <CustomTypography
                        props={{
                          color: "#2F2B3DB2",
                          fontWeight: 400,
                          fontSize: "12px",
                          lineHeight: "28px",
                        }}
                      >
                        {stepSelectedCount}/{stepTotalCount}{" "}
                        {t("categories_selected", {
                          ns: "supplier-products-info",
                        })}
                      </CustomTypography>
                    )}
                    {/* Back is desktop-only — on mobile the tabs handle step
                        navigation — and only when there is a preceding step. */}
                    {!isMobile && prevStep && (
                      <Button
                        variant="outlined"
                        sx={{
                          ...actionButtonStyle,
                          backgroundColor: "transparent",
                          color: "rgba(62, 63, 76, 1)",
                          border: "1px solid rgba(62, 63, 76, 0.3)",
                          "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                          width: isCustomScreen ? "100%" : "auto",
                        }}
                        onClick={() => setActiveStep(prevStep)}
                      >
                        {t("button_back", { ns: "supplier-products-info" })}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      sx={{
                        ...actionButtonStyle,
                        width: isCustomScreen ? "100%" : "auto",
                      }}
                      disabled={
                        selectedPricelists.length === 0 &&
                        selectedCategoryIds.length === 0 &&
                        selectedRestaurantCategoryIds.length === 0
                      }
                      onClick={handleAcceptAll}
                    >
                      {t("accept_selected_continue", {
                        ns: "supplier-products-info",
                      })}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ) : (
              <ModalActionButtons
                {...{
                  viewType,
                  inviteBlockedReason,
                  handleInviteClick,
                  handleAcceptOffer,
                  handleUpdateSupplier,
                  handleDeclineOfferModal,
                  handleConfirmModal,
                  handleRemoveSupplierModal,
                  selectedPricelists,
                  pricelists,
                  supplier,
                }}
              />
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <InviteModal
        {...{
          openSuccess,
          handleClose: handleCloseInviteModal,
          email: supplier.email,
          phonePrefix: supplier.phonePrefix,
          phoneNumber: supplier.phoneNumber,
          whatsappPrefix: supplier.whatsappPrefix,
          whatsappNumber: supplier.whatsappNumber,
          name: supplier.name,
          title: t("title", { ns: "invite-modal" }),
          successMessage: t("success_message", { ns: "invite-modal" }),
          successMessageDescription: t("subtitle", {
            ns: "invite-modal",
          }),
        }}
      />

      <SupplierAcceptedModal
        {...{
          supplier,
          supplierProfileData,
          selectedPricelists,
          acceptedItems,
          openSupplierAccept,
          handleCloseSupplierAcceptModal,
        }}
      />

      <RemoveSupplierModal
        {...{
          viewType,
          supplier,
          openRemoveSupplier,
          setOpenRemoveSupplier,
          onRemove:
            viewType === "offer" ? handleDeclineOffer : handleSupplierRemoved,
          // Removal is blocked while orders are open; the modal offers the pause
          // instead — unless the supplier is already paused, in which case there
          // is nothing left to do but wait for the orders to close.
          openOrdersCount: viewType === "view" ? openOrdersCount : 0,
          isPaused: is_paused,
          onPauseInstead: handlePauseInstead,
          pausingSupplier,
        }}
      />

      <DiscardChangesModal
        {...{
          openDiscardChangesModal,
          setOpenDiscardChangesModal,
          handleDiscard,
        }}
      />

      <MaximumCapacityReachedModal
        open={capacityBlockedSupplier !== null}
        viewer="cm"
        supplierName={capacityBlockedSupplier}
        onClose={() => {
          setCapacityBlockedSupplier(null);
          handleCloseSupplierProfileModal();
        }}
      />
    </>
  );
};

export default SupplierProfileModal;
